import React from 'react';

import { FormattedMessage } from 'react-intl';

import { AirplanemodeActive, Hotel, PlayCircle } from '@mui/icons-material';
import SlideshowRoundedIcon from '@mui/icons-material/SlideshowRounded';
import { CoreUtils, IdEnums, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import commonMessages from '~/core/commonMessages';
import { GameCenterIcon, PromotionIcon, TicketsIcon } from '~/core/customIcons';
import { PageName } from '~/core/enums';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useShouldForceVideoLanguage } from '~/core/hooks/useShouldForceVideoLanguage';
import { getEnvironmentAwareHref, showGameCenterLink, showTicketsLink } from '~/core/utils';
import { PlayoffSeriesGame } from '~/views/pages/dotcom/playoffSeries/types';
import { GameLink, ScheduleGame } from '~/views/pages/dotcom/schedule/types';

import { SpecialEvent } from './enums';

import { NonLinkedTag } from './styles';

type GameLinksProp = {
    game: ScheduleGame | PlayoffSeriesGame;
    isWebview: boolean;
    pageName: PageName;
    showOnlyHomeTickets: boolean;
    streamUrl?: string;
    teamTriCode?: string;
    threeMinuteRecap?: string;
    condensedGame?: string;
    ticketsLink?: string;
    onHowToWatchClick?: (gameId: number | null) => void;
};

export const useLinksWithRecap = (gameLinksProp: GameLinksProp): Array<GameLink> => {
    const shouldForceVideoLanguage = useShouldForceVideoLanguage();

    gameLinksProp.threeMinuteRecap = useOptionForLocale(
        {
            [ShortCodeEnums.Language.English]: (gameLinksProp.game as ScheduleGame).threeMinRecap,
            [ShortCodeEnums.Language.French]: (gameLinksProp.game as ScheduleGame).threeMinRecapFr,
        },
        shouldForceVideoLanguage
    );

    gameLinksProp.condensedGame = useOptionForLocale(
        {
            [ShortCodeEnums.Language.English]: (gameLinksProp.game as ScheduleGame).condensedGame,
            [ShortCodeEnums.Language.French]: (gameLinksProp.game as ScheduleGame).condensedGameFr,
        },
        shouldForceVideoLanguage
    );

    return getGameLinks(gameLinksProp);
};

export const getGameLinks = ({
    game,
    isWebview,
    pageName,
    showOnlyHomeTickets,
    teamTriCode,
    threeMinuteRecap,
    condensedGame,
    ticketsLink,
    onHowToWatchClick,
}: GameLinksProp): Array<GameLink> => {
    const hasHowToWatchModal = !!onHowToWatchClick;

    const isShowingHowToWatchIcon: boolean =
        (CoreUtils.isGameActive(game.gameState) || CoreUtils.isPregame(game.gameState)) && hasHowToWatchModal;

    return [
        {
            icon: <SlideshowRoundedIcon />,
            message: (
                <NonLinkedTag onClick={() => onHowToWatchClick?.(game.id)}>
                    <FormattedMessage {...commonMessages.linkDescriptions.howToWatch} />
                </NonLinkedTag>
            ),
            // TODO - remove language requirement
            show:
                isShowingHowToWatchIcon &&
                [ShortCodeEnums.Language.English, ShortCodeEnums.Language.French].includes(
                    window.__SITE_SETTINGS__.languageCode as ShortCodeEnums.Language
                ),
            onClick: () => onHowToWatchClick?.(game.id),
        },
        {
            actionUrl: ticketsLink,
            icon: <TicketsIcon />,
            message: <FormattedMessage {...commonMessages.linkDescriptions.tickets} />,
            show:
                !!ticketsLink &&
                showTicketsLink(game) &&
                (showOnlyHomeTickets && teamTriCode ? teamTriCode === game.homeTeam.abbrev.toLowerCase() : true),
        },
        {
            actionUrl: threeMinuteRecap,
            icon: <PlayCircle />,
            message: <FormattedMessage {...commonMessages.linkDescriptions.recap} />,
            show: CoreUtils.isPostGame(game.gameState) && !!threeMinuteRecap && pageName !== PageName.PlayoffSeries,
            isVideoLink: true,
        },
        {
            actionUrl: condensedGame,
            icon: <PlayCircle />,
            message: <FormattedMessage {...commonMessages.linkDescriptions.condensed} />,
            show: CoreUtils.isPostGame(game.gameState) && !!condensedGame && pageName !== PageName.PlayoffSeries,
            isVideoLink: true,
        },
        {
            actionUrl: game.gameCenterLink && getEnvironmentAwareHref(game.gameCenterLink, isWebview),
            icon: <GameCenterIcon />,
            message: <FormattedMessage {...commonMessages.linkDescriptions.gamecenter} />,
            show: showGameCenterLink(game),
        },
    ].filter((link) => link.show);
};

export const getPartnerLinks = (game: ScheduleGame, isHomeGame: boolean): Array<GameLink> => {
    const preGame = CoreUtils.isPregame(game.gameState);
    const airlineDesc = isHomeGame ? game.homeTeam.airlineDesc : game.awayTeam.airlineDesc;
    const airlineUrl = isHomeGame ? game.homeTeam.airlineLink : game.awayTeam.airlineLink;
    const hotelDesc = isHomeGame ? game.homeTeam.hotelDesc : game.awayTeam.hotelDesc;
    const hotelUrl = isHomeGame ? game.homeTeam.hotelLink : game.awayTeam.hotelLink;

    // We were expecting no Description/URL, but we can get an empty string.
    const hasText = (text?: string): boolean => (text || '') !== '';

    return [
        {
            actionUrl: airlineUrl,
            icon: <AirplanemodeActive />,
            message: airlineDesc,
            show: preGame && hasText(airlineDesc) && hasText(airlineUrl),
        },
        {
            actionUrl: hotelUrl,
            icon: <Hotel />,
            message: hotelDesc,
            show: preGame && hasText(hotelDesc) && hasText(hotelUrl),
        },
        {
            actionUrl: game.homeTeam.promoLink,
            icon: <PromotionIcon />,
            message: game.homeTeam.promoDesc,
            show: preGame && isHomeGame && hasText(game.homeTeam.promoDesc) && hasText(game.homeTeam.promoLink),
        },
    ].filter((link) => link.show);
};

export const getEventType = (game: ScheduleGame): SpecialEvent => {
    let eventType = SpecialEvent.none;
    if (game.gameType === IdEnums.GameType.playoffs) {
        eventType = SpecialEvent.playoffs;
    } else if (game.specialEvent) {
        eventType = SpecialEvent.event;
    } else if (game.gameType === IdEnums.GameType.preseason) {
        eventType = SpecialEvent.preseason;
    }
    return eventType;
};
